import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type {
  APIResponse,
  MatterStatus,
  MatterStatusCategory,
  MatterSettings as MatterSettingsType,
} from "@/lib/types";
import { concatArraysCustomizer } from "@/lib/utils/axiosUtils";
import { mergeWith, orderBy } from "lodash";
import { retrieveMatterStatusCategoriesV2 } from "./matter-status-category";

export type MatterSettings = MatterSettingsType;

const matterSettingsRoute = "matters/v2/settings";

const joinMatterStatusCategories = (
  settings: MatterSettings,
  categories: MatterStatusCategory[],
): MatterSettings => {
  const categoriesById = new Map(
    categories.map((category) => [category.id, category]),
  );
  const categoryOrderById = new Map(
    categories.map((category, index) => [category.id, index]),
  );

  const statuses = settings.statuses.map(
    (status): MatterStatus => ({
      ...status,
      category: categoriesById.get(status.categoryId) ?? {
        dateCreated: null,
        dateModified: null,
        description: null,
        id: status.categoryId,
        label: status.categoryId,
      },
    }),
  );

  return {
    ...settings,
    statuses: orderBy(
      statuses,
      [
        (status) =>
          categoryOrderById.get(status.categoryId) ?? Number.POSITIVE_INFINITY,
        "categoryId",
        (status) => status.order ?? Number.POSITIVE_INFINITY,
        "label",
        "id",
      ],
      ["asc", "asc", "asc", "asc", "asc"],
    ),
  };
};

/**
 * Retrieve all matter settings, following pagination until complete.
 * The backend may return a `pagination.lastEvaluatedKey` when there is more data.
 */
export async function retrieveMatterSettingsV2() {
  // Fetch first page to seed accumulator and meta
  const [firstRes, statusCategoriesRes] = await Promise.all([
    authenticatedFastApiAxiosInstance.get<APIResponse<MatterSettings>>(
      matterSettingsRoute,
    ),
    retrieveMatterStatusCategoriesV2(),
  ]);
  let acc = firstRes.data.data;
  let meta = firstRes.data.meta;
  let cursor = firstRes.data.pagination?.lastEvaluatedKey;

  // Safety to avoid infinite loops in case of server bug
  const maxPages = 100;
  let pageCount = 1;

  while (cursor && pageCount < maxPages) {
    const res = await authenticatedFastApiAxiosInstance.get<
      APIResponse<MatterSettings>
    >(matterSettingsRoute, { params: { lastEvaluatedKey: cursor } });
    const page = res.data;
    acc = mergeWith({}, acc, page.data, concatArraysCustomizer);
    meta = page.meta;
    cursor = page.pagination?.lastEvaluatedKey;
    pageCount += 1;
  }

  return {
    data: joinMatterStatusCategories(acc, statusCategoriesRes.data),
    meta,
  } satisfies APIResponse<MatterSettings>;
}
